import React from 'react';
import { StyleSheet, ScrollView } from 'react-native';
import { useTranslation } from 'react-i18next';
import { Button } from '@rneui/themed';

import { post_reservation } from '../api/reservation';
import { ActivityModal } from 'components/modal/Modal';
import { addDataToInfoMap } from 'components/Dialog/infoMap';
import Dialog from 'components/Dialog/Dialog';
import { type FinalReservationType } from 'types/Reservation';
import {
  NOT_AVAILABLE_CODE,
  NOT_FOUND_CODE,
  SUCCESS_MESSAGE_CODE,
  VALIDATION_MESSAGE_CODE,
} from 'const/messagesCode';
import { type SubmitReservationType } from 'types/API';
import { type ReservationConfirmProps } from 'types/ScreenTypes';

const styles = StyleSheet.create({
  container: {
    width: '100%',
    padding: 24,
    backgroundColor: '#fff',
    alignItems: 'center',
    justifyContent: 'center',
  },
});

const ReservationConfirmScreen: React.FC<ReservationConfirmProps> = ({
  navigation,
  route,
}) => {
  const value = route.params;
  const actions = value?.actions;
  const { t } = useTranslation();

  const [messageItem, setMessageItem] = React.useState<{
    message_code: number;
    message: string;
  }>({
    message_code: 0,
    message: '',
  });
  const [modalState, setModalState] = React.useState({
    isVisible: false,
    loading: false,
  });

  const columnInfoMap = {
    name: {
      name: t('name'),
      type: 'text',
    },
    date: {
      name: t('date'),
      type: 'text',
    },
    time: {
      name: t('time'),
      type: 'text',
    },
    people_num: {
      name: t('peopleNumber'),
      type: 'text',
    },
    email: {
      name: t('email'),
      type: 'text',
    },
    tel: {
      name: t('phoneNumber'),
      type: 'text',
    },
    table: {
      name: t('table'),
      type: 'image',
    },
    options: {
      name: t('options'),
      type: 'listText',
    },
    accommodation: {
      name: t('accommodation'),
      type: 'text',
    },
    notes: {
      name: t('notes'),
      type: 'text',
    },
  };

  const handleFinalModal = (): void => {
    setModalState({
      isVisible: false,
      loading: modalState.loading,
    });
    if (messageItem.message_code === SUCCESS_MESSAGE_CODE) {
      actions.resetForm();
      navigation.navigate('Reservation');
    } else if (messageItem.message_code === VALIDATION_MESSAGE_CODE) {
      navigation.navigate('Reservation');
    } else if (
      messageItem.message_code === NOT_FOUND_CODE ||
      messageItem.message_code === NOT_AVAILABLE_CODE
    ) {
      actions.resetForm();
      navigation.navigate('Reservation');
    } else {
      actions.resetForm();
      navigation.navigate('Reservation');
    }
  };

  const afterSubmit = (data: SubmitReservationType): void => {
    let message = '';
    if (data?.message_code === SUCCESS_MESSAGE_CODE) {
      message = t('reservationSuccess');
    } else if (data?.message_code === VALIDATION_MESSAGE_CODE) {
      message = 'Validation Error';
    } else if (
      data?.message_code === NOT_FOUND_CODE ||
      data?.message_code === NOT_AVAILABLE_CODE
    ) {
      message = t('reservationAlreadyExists');
    } else {
      message = t('serverError');
    }

    setMessageItem({
      message,
      message_code: data?.message_code,
    });
    setModalState({
      isVisible: true,
      loading: false,
    });
  };

  const onSubmit = (): void => {
    setModalState({
      isVisible: true,
      loading: true,
    });

    const { table, options, ...rest } = value;

    if (!table) {
      setMessageItem({
        message: t('table') + ' ' + t('required'),
        message_code: VALIDATION_MESSAGE_CODE,
      });
      setModalState({
        isVisible: true,
        loading: false,
      });
      return;
    }

    const tableId = table.table_id ?? table.id ?? '';
    const tableTypeId = table.type_id ?? '';
    const normalizedOptions = options.map((option) => option.id);

    const submitValue: FinalReservationType = {
      ...rest,
      table_id: tableId,
      table_type_id: tableTypeId,
      options: normalizedOptions,
    };

    submitValue.tel = submitValue.tel.replace(/ /g, '-');
    void post_reservation(submitValue, afterSubmit);
  };

  return (
    <ScrollView contentContainerStyle={styles.container}>
      <ActivityModal
        modalState={modalState}
        closeFunc={handleFinalModal}
        message={messageItem?.message}
      />
      <Dialog
        data={addDataToInfoMap(value, columnInfoMap)}
        title={t('reservationConfirmation')}
      />
      <Button title={t('reserve')} onPress={onSubmit} />
    </ScrollView>
  );
};

export default ReservationConfirmScreen;
